import { ArrowLeft, Mail, Phone, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* Öffentliche Hilfeseite für die Mitglieder-App.
   Sie ist die Support-Adresse, die im App Store hinterlegt wird — Apple ruft
   sie bei jeder Prüfung auf (Review Guideline 1.5). Deshalb muss sie ohne
   Anmeldung erreichbar sein und echte Kontaktwege nennen. */

const FRAGEN = [
  {
    frage: "Wie melde ich mich an?",
    antwort: (
      <>
        Mit der E-Mail-Adresse, die im Studio hinterlegt ist, und deiner PIN.
        Die PIN sind die <strong>letzten vier Ziffern deiner Handynummer</strong>.
        Wenn du dich zum ersten Mal anmeldest und es nicht klappt, liegt bei uns
        wahrscheinlich eine andere E-Mail oder Nummer — sag am Empfang Bescheid
        oder schreib uns eine kurze Mail.
      </>
    ),
  },
  {
    frage: "Die Anmeldung sagt „Zu viele Versuche“.",
    antwort: (
      <>
        Nach fünf Fehlversuchen sperren wir die Anmeldung für etwa 15 Minuten.
        Das schützt dein Konto davor, dass jemand die vierstellige PIN durchprobiert.
        Warte kurz und versuch es dann erneut.
      </>
    ),
  },
  {
    frage: "Ich bin schon Mitglied, aber neu in der App.",
    antwort: (
      <>
        Tippe auf dem Anmeldebildschirm auf <em>Zugang aktivieren</em> und gib
        Vor- und Nachnamen sowie deine Handynummer ein. Passen die Angaben zu
        unseren Unterlagen, bist du sofort drin. Falls nicht, kannst du die
        Angaben an den Empfang schicken — wir prüfen sie von Hand.
      </>
    ),
  },
  {
    frage: "Wie buche ich einen Kurs, und wie sage ich wieder ab?",
    antwort: (
      <>
        Im Reiter <em>Kurse</em> siehst du den Stundenplan mit den freien Plätzen.
        Ein Tipp auf <em>Jetzt buchen</em> reicht. Ist der Kurs voll, kommst du auf
        die Warteliste und rückst automatisch nach, sobald jemand absagt. Deine
        Buchungen findest du im Profil unter <em>Kurse</em>, dort kannst du sie
        auch stornieren.
      </>
    ),
  },
  {
    frage: "Was ist ein Stammplatz?",
    antwort: (
      <>
        Ein Stammplatz bucht dich jede Woche automatisch in denselben Kurs. Du
        musst nichts weiter tun und kannst einzelne Termine trotzdem absagen,
        wenn du mal nicht kannst.
      </>
    ),
  },
  {
    frage: "Die Türöffnung funktioniert nicht.",
    antwort: (
      <>
        Die Tür lässt sich nur <strong>innerhalb von zehn Minuten nach einem
        Check-in</strong> öffnen, und nur bei laufender Mitgliedschaft. Scanne also
        zuerst am Empfang ein. Erscheint danach weiter eine Fehlermeldung, melde
        dich bei uns — dann stimmt etwas mit der Mitgliedschaft nicht.
      </>
    ),
  },
  {
    frage: "Warum zählt mein Training nicht?",
    antwort: (
      <>
        Gezählt werden Check-ins am Empfang und Einheiten, die du im Reiter
        <em> Training</em> protokollierst. Pro Tag nehmen wir höchstens acht
        protokollierte Einheiten an — das verhindert, dass sich jemand Gurte und
        Prämienpunkte zusammenklickt.
      </>
    ),
  },
  {
    frage: "Wie lösche ich mein Konto?",
    antwort: (
      <>
        Im Profil ganz unten findest du <em>Konto löschen</em>. Der Zugang zur App
        endet damit sofort. Hast du die App nicht mehr installiert, schreib uns an{" "}
        <a href="mailto:info@p1-pforzheim.de">info@p1-pforzheim.de</a> — wir löschen
        dein Konto dann von Hand. Deine Mitgliedschaft im Studio endet dadurch nicht —
        dafür gilt weiter dein Vertrag. Unterlagen zu Vertrag und Zahlungen müssen
        wir gesetzlich aufbewahren (§ 147 AO, § 257 HGB), alles Übrige löschen wir.
      </>
    ),
  },
  {
    frage: "Welche Daten verarbeitet die App?",
    antwort: (
      <>
        Das steht ausführlich in unserer{" "}
        <a href="/datenschutz" className="text-[#EF7C22] underline">Datenschutzerklärung</a>,
        Abschnitt 8 beschreibt die App im Einzelnen.
      </>
    ),
  },
];

export default function AppHilfePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="max-w-3xl mx-auto px-6 py-16">
        <button
          onClick={() => navigate(-1)}
          data-testid="app-hilfe-back-btn"
          className="flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#0A0A0A] mb-8"
        >
          <ArrowLeft size={16} /> Zurück
        </button>

        <h1 className="text-3xl font-bold text-[#0A0A0A] mb-3">Hilfe zur P1-App</h1>
        <p className="text-[#4B5563] leading-relaxed mb-10">
          Die Mitglieder-App des P1 Performance Center. Wenn deine Frage hier nicht
          steht, schreib uns einfach — wir antworten in der Regel am selben Tag.
        </p>

        <div className="rounded-xl border border-[#E5E7EB] p-6 mb-12 space-y-3">
          <a href="mailto:info@p1-pforzheim.de" className="flex items-center gap-3 text-[#0A0A0A] hover:text-[#EF7C22]">
            <Mail size={18} className="text-[#EF7C22]" /> info@p1-pforzheim.de
          </a>
          <a href="tel:+4917686477728" className="flex items-center gap-3 text-[#0A0A0A] hover:text-[#EF7C22]">
            <Phone size={18} className="text-[#EF7C22]" /> 0176 86477728
          </a>
          <div className="flex items-start gap-3 text-[#4B5563]">
            <MapPin size={18} className="text-[#EF7C22] mt-0.5 shrink-0" />
            <span>
              P1 Performance Center GmbH<br />
              Georg-Stieß-Straße 2, 75181 Pforzheim
            </span>
          </div>
        </div>

        <div className="space-y-8">
          {FRAGEN.map(({ frage, antwort }) => (
            <section key={frage}>
              <h2 className="text-lg font-bold text-[#0A0A0A] mb-2">{frage}</h2>
              <p className="text-[#4B5563] leading-relaxed">{antwort}</p>
            </section>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-[#E5E7EB] flex gap-6 text-sm text-[#4B5563]">
          <a href="/datenschutz" className="hover:text-[#0A0A0A]">Datenschutz</a>
          <a href="/impressum" className="hover:text-[#0A0A0A]">Impressum</a>
        </div>
      </div>
    </div>
  );
}
